// components/FlashBarAdmin.jsx
import React, { useState, useEffect } from 'react';
import FlashBar from '../components/FlashBar';
import axios from "axios";
import config from '../config/config';
import CmsMain from '../pages/CmsMain';

const FlashBarAdmin = ({ slug = '', onSave, onClose }) => {
  const token = localStorage.getItem("token");

  // Default / fallback values
  const defaultConfig = {
    enabled: true,
    preset: 'clean-light',
    dismissible: true,
    devMode: false,
    messages: [{
      prefix: 'New Collection',
      text: '25% off sitewide — Limited time',
      ctaText: 'Shop Now',
      link: '/sale',
      countdownEnd: '',
    }],
    customStyles: {
      backgroundColor: '',
      textColor: '',
      ctaColor: '',
      height: '',
      prefixTextColor: '',
      prefixIcon: '',
      prefixIconColor: '',
    }
  };

  // Form state
  const [enabled, setEnabled]             = useState(defaultConfig.enabled);
  const [preset, setPreset]               = useState(defaultConfig.preset);
  const [dismissible, setDismissible]     = useState(defaultConfig.dismissible);
  const [devMode, setDevMode]             = useState(defaultConfig.devMode);

  const [prefix, setPrefix]               = useState(defaultConfig.messages[0].prefix);
  const [text, setText]                   = useState(defaultConfig.messages[0].text);
  const [ctaText, setCtaText]             = useState(defaultConfig.messages[0].ctaText);
  const [link, setLink]                   = useState(defaultConfig.messages[0].link);
  const [countdownDate, setCountdownDate] = useState(defaultConfig.messages[0].countdownEnd);

  const [backgroundColor, setBackgroundColor]   = useState(defaultConfig.customStyles.backgroundColor);
  const [textColor, setTextColor]               = useState(defaultConfig.customStyles.textColor);
  const [ctaColor, setCtaColor]                 = useState(defaultConfig.customStyles.ctaColor);
  const [height, setHeight]                     = useState(defaultConfig.customStyles.height);
  const [prefixTextColor, setPrefixTextColor]   = useState(defaultConfig.customStyles.prefixTextColor);
  const [prefixIcon, setPrefixIcon]             = useState(defaultConfig.customStyles.prefixIcon);
  const [prefixIconColor, setPrefixIconColor]   = useState(defaultConfig.customStyles.prefixIconColor);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  // Load settings — global or custom (variant) depending on slug
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadSettings = async () => {
      if (!token) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        setLoading(true);

        await axios.get(config.API_URL + "/sanctum/csrf-cookie", {
          withCredentials: true,
          signal: controller.signal,
        });

        let response;
        const isCustomVariant = slug && slug.includes('-');

        if (isCustomVariant) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true,
              signal: controller.signal,
            }
          );
          applyData(response.data.customSettings || {});
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullFlashBarSettings',
            {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true,
              signal: controller.signal,
            }
          );
          applyData(response.data.flashBarSettings || {});
        }
      } catch (err) {
        if (err.name !== 'CanceledError') {
          console.error('Failed to load flashbar settings:', err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const applyData = (dbData) => {
      setEnabled(dbData.enabled ?? defaultConfig.enabled);
      setPreset(dbData.preset ?? defaultConfig.preset);
      setDismissible(dbData.dismissible ?? defaultConfig.dismissible);
      setDevMode(dbData.devMode ?? defaultConfig.devMode);

      const msg = dbData.messages?.[0] || defaultConfig.messages[0];
      setPrefix(msg.prefix ?? defaultConfig.messages[0].prefix);
      setText(msg.text ?? defaultConfig.messages[0].text);
      setCtaText(msg.ctaText ?? defaultConfig.messages[0].ctaText);
      setLink(msg.link ?? defaultConfig.messages[0].link);
      setCountdownDate(msg.countdownEnd ?? defaultConfig.messages[0].countdownEnd);

      const styles = dbData.customStyles || defaultConfig.customStyles;
      setBackgroundColor(styles.backgroundColor ?? '');
      setTextColor(styles.textColor ?? '');
      setCtaColor(styles.ctaColor ?? '');
      setHeight(styles.height ?? '');
      setPrefixTextColor(styles.prefixTextColor ?? '');
      setPrefixIcon(styles.prefixIcon ?? '');
      setPrefixIconColor(styles.prefixIconColor ?? '');
    };

    loadSettings();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [token, slug]);

  // Current config object (used for preview and save)
  const currentConfig = {
    enabled,
    preset,
    dismissible,
    devMode,
    messages: [{
      prefix: preset === 'clean-light' ? prefix : undefined,
      text,
      ctaText: preset === 'clean-light' ? ctaText : undefined,
      link: preset === 'clean-light' ? link : undefined,
      countdownEnd: preset === 'premium-dark' ? countdownDate : undefined,
    }],
    customStyles: devMode ? {
      backgroundColor: backgroundColor || undefined,
      textColor: textColor || undefined,
      ctaColor: ctaColor || undefined,
      height: height || undefined,
      prefixTextColor: prefixTextColor || undefined,
      prefixIcon: prefixIcon || undefined,
      prefixIconColor: prefixIconColor || undefined,
    } : {},
  };

  const saveSettings = async () => {
    if (!token) {
      alert("Authentication required");
      return;
    }

    try {
      setSaving(true);

      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const isCustomVariant = slug && slug.includes('-');
      const saveEndpoint = isCustomVariant
        ? `${config.API_URL}/api/saveFlashBarSettings/${slug}`
        : `${config.API_URL}/api/saveFlashBarSettings`;

      const payload = { settings: currentConfig };

      const res = await axios.post(
        saveEndpoint,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
          },
          withCredentials: true,
        }
      );

      if (res.data.status === 1) {
        alert("Flash bar settings saved successfully!");
      } else {
        alert("Save failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading flash bar settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b px-6 py-5 shadow-sm sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-gray-900">
          Flash Bar Editor
          {slug && slug.includes('-') && ` — variant (${slug})`}
        </h1>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT — Controls */}
        <div className="space-y-7">

          {/* Enable Toggle */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Enable Flash Bar</h3>
                <p className="text-sm text-gray-500 mt-0.5">Display at the top of the site</p>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(prev => !prev)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  enabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {enabled && (
            <>
              {/* Preset Selector */}
              <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Design Template</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { value: 'clean-light', label: 'Clean Light', desc: 'Minimal white style with CTA' },
                    { value: 'premium-dark', label: 'Premium Dark', desc: 'Urgent dark style with countdown' },
                  ].map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPreset(p.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        preset === p.value
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">{p.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-xl shadow border border-gray-200 p-6 space-y-5">
                <h3 className="text-lg font-semibold">Message Content</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Main Message</label>
                  <input
                    type="text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                    placeholder="Summer sale  — up to 40% off"
                  />
                </div>

                {preset === 'clean-light' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Prefix</label>
                      <input
                        type="text"
                        value={prefix}
                        onChange={e => setPrefix(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                        placeholder="Hot Drop"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">CTA Text</label>
                        <input
                          type="text"
                          value={ctaText}
                          onChange={e => setCtaText(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Link URL</label>
                        <input
                          type="text"
                          value={link}
                          onChange={e => setLink(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                          placeholder="/collections/sale"
                        />
                      </div>
                    </div>
                  </>
                )}

                {preset === 'premium-dark' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Countdown End</label>
                    <input
                      type="datetime-local"
                      value={countdownDate ? countdownDate.slice(0, 16) : ''}
                      onChange={e => {
                        const val = e.target.value;
                        setCountdownDate(val ? new Date(val).toISOString() : '');
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Dismissible</label>
                    <p className="text-xs text-gray-500">Visitors can close the bar</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDismissible(prev => !prev)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      dismissible ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        dismissible ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Advanced / Dev Mode */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-900">Advanced Customization</h3>
                    <p className="text-sm text-amber-800 mt-1">Override colors, height, icons...</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDevMode(prev => !prev)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                      devMode ? 'bg-amber-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        devMode ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {devMode && (
                  <div className="mt-6 space-y-5 border-t border-amber-200 pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-amber-900">Background</label>
                        <input
                          type="text"
                          value={backgroundColor}
                          onChange={e => setBackgroundColor(e.target.value)}
                          placeholder="#000 or linear-gradient(to right, #111, #333)"
                          className="mt-1 block w-full px-3 py-2 border border-amber-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-amber-900">Text Color</label>
                        <input
                          type="color"
                          value={textColor || '#000000'}
                          onChange={e => setTextColor(e.target.value)}
                          className="mt-1 h-10 w-full rounded border border-amber-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium">Prefix Icon</label>
                        <select
                          value={prefixIcon}
                          onChange={e => setPrefixIcon(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="">None</option>
                          <option value="Sparkle">Sparkle</option>
                          <option value="Sparkles">Sparkles</option>
                          <option value="Percent">Percent</option>
                          <option value="CirclePercent">Circle Percent</option>
                          <option value="Dessert">Dessert</option>
                          <option value="CandyCane">Candy Cane</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium">Prefix Icon Color</label>
                        <input
                          type="color"
                          value={prefixIconColor || '#000000'}
                          onChange={e => setPrefixIconColor(e.target.value)}
                          className="mt-1 h-10 w-full rounded border border-amber-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label className="block text-sm font-medium">CTA Color</label>
                        <input
                          type="color"
                          value={ctaColor || '#000000'}
                          onChange={e => setCtaColor(e.target.value)}
                          className="mt-1 h-10 w-full rounded border border-amber-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">Prefix Text</label>
                        <input
                          type="color"
                          value={prefixTextColor || '#000000'}
                          onChange={e => setPrefixTextColor(e.target.value)}
                          className="mt-1 h-10 w-full rounded border border-amber-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">Height</label>
                        <input
                          type="text"
                          value={height}
                          onChange={e => setHeight(e.target.value)}
                          placeholder="56px or 3rem"
                          className="mt-1 block w-full px-3 py-2 border border-amber-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={saveSettings}
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl disabled:opacity-60 transition shadow-md"
              >
                {saving ? 'Saving…' : 'Save & Publish'}
              </button>
            </>
          )}
        </div>

        {/* RIGHT — Previews */}
        <div className="space-y-6 sticky top-6 self-start">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-100 px-5 py-3 border-b">
              <h3 className="font-semibold text-gray-800">Component Preview</h3>
            </div>
            <div className="p-4 bg-gradient-to-b from-sky-50 to-gray-100">
              <FlashBar config={currentConfig} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-100 px-5 py-3 border-b">
              <h3 className="font-semibold text-gray-800">Page Context Preview</h3>
            </div>
            <div className="min-h-[60vh] bg-gradient-to-b from-sky-50 to-gray-100 p-4">
              <CmsMain />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashBarAdmin;